import { DateTime } from 'luxon'
import { toThreeLetterTimezone } from 'utils/timezones'

// "Thu, Oct 29 · 9:00 AM – 10:00 AM EDT". Returns null for an invalid slot so
// callers can hide the row instead of printing "Invalid DateTime".
export const formatSlotRange = (startDateTime, durationInMinutes, timezone) => {
  if (!startDateTime) return null
  const start = DateTime.fromISO(startDateTime, { zone: timezone }).setLocale(
    'en-US',
  )
  if (!start.isValid) return null
  const end = start.plus({ minutes: Number(durationInMinutes) || 0 })
  const zone = toThreeLetterTimezone(timezone, start)
  const range = `${start.toFormat("ccc, LLL d '·' h:mm a")} – ${end.toFormat(
    'h:mm a',
  )}`
  return zone ? `${range} ${zone}` : range
}

const scheduleBase = ({ idOrSlug, lessonTypeId, locationId }) =>
  `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}`

// Pencil on "Your time": back to step 1. A coach's `?student=` is kept so the
// pre-selected student survives, and `selectedDay` opens step 1 on the day the
// slot was picked.
export const buildChangeTimeUrl = ({
  idOrSlug,
  lessonTypeId,
  locationId,
  studentParam,
  lessonCreditBalanceQuantity,
  startDateTime,
  timezone,
}) => {
  let url = scheduleBase({ idOrSlug, lessonTypeId, locationId })
  if (studentParam) url += `&student=${studentParam}`
  if (lessonCreditBalanceQuantity) {
    url += `&lessonCreditBalanceQuantity=${lessonCreditBalanceQuantity}`
  }
  const start = startDateTime
    ? DateTime.fromISO(startDateTime, { zone: timezone })
    : null
  if (start?.isValid) url += `&selectedDay=${start.toISODate()}`
  return url
}

// Pencil on the student row: back to step 2 with the slot kept. `?student=` is
// dropped on purpose, because step 2 skips straight ahead when it is present.
export const buildChangeStudentUrl = ({
  idOrSlug,
  lessonTypeId,
  locationId,
  lessonCreditBalanceQuantity,
  startDateTime,
}) => {
  let url = `${scheduleBase({
    idOrSlug,
    lessonTypeId,
    locationId,
  })}&startDateTime=${encodeURIComponent(startDateTime)}`
  if (lessonCreditBalanceQuantity) {
    url += `&lessonCreditBalanceQuantity=${lessonCreditBalanceQuantity}`
  }
  return url
}

// "Adult" / "Junior" and, when we know it, the booking email.
export const getStudentSubtitle = ({
  student,
  guest,
  user,
  coachBooksOnBehalfOfStudent,
}) => {
  const isMinor = guest ? guest.student?.isMinor : student?.isMinor
  const ageLabel = isMinor == null ? null : isMinor ? 'Junior' : 'Adult'
  let email = null
  if (guest) {
    email = guest.contact?.email
  } else if (coachBooksOnBehalfOfStudent) {
    // The coach's own address is not the student's, so use the student's.
    email = student?.representatives?.find(r => r?.relationshipType === 'SELF')
      ?.contact?.email
  } else {
    email = user?.email
  }
  return [ageLabel, email].filter(Boolean).join(' · ')
}

export const formatLessonLine = lessonType =>
  [
    lessonType?.title,
    lessonType?.durationInMinutes && `${lessonType.durationInMinutes} min`,
  ]
    .filter(Boolean)
    .join(' · ')
